/*
 * Client for Zana's ranged reports and their schedules.
 *
 * The document itself is built server-side (see premium/reports/*) and arrives
 * base64-encoded. That is deliberate: the SAME builder produces the file the
 * cron emails at 06:00, when there is no browser to render one. Generating the
 * PDF here as well would give us two definitions of "the report" that drift.
 */

import { auth } from '../firebase';

const BASE = (process.env.REACT_APP_PAYMENTS_URL || '').replace(/\/$/, '');

export interface ReportFile {
  filename: string;
  mime: string;
  base64: string;
  periodLabel: string;
  from: string;
  to: string;
  vehicles: number;
  notes: string[];
  totals: { runs: number; km: number; fuelKES: number; incidents: number; alerts: number };
}

export interface ReportSchedule {
  id: string;
  cadence: 'daily' | 'weekly' | 'monthly';
  format: 'pdf' | 'csv';
  hourUtc: number;
  weekday: number | null;
  dayOfMonth: number | null;
  email: string;
  nextRunAtMs: number | null;
  describe: string;
}

async function authedPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
  if (!BASE) throw new Error('The reports service is not configured for this build.');
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in again before running that.');
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok || json.ok !== true) {
    throw new Error(String(json.message || 'That did not go through. Try again in a moment.'));
  }
  return json as T;
}

export async function generateReport(
  orgCode: string,
  opts: { preset?: string; from?: string; to?: string; format?: string },
): Promise<ReportFile> {
  return authedPost<ReportFile>('/api/premium/zana/report', { orgCode, ...opts });
}

/** Turn the base64 payload into a real download. */
export function saveReportFile(file: { filename: string; mime: string; base64: string }): void {
  const binary = atob(file.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const url = URL.createObjectURL(new Blob([bytes], { type: file.mime }));
  const a = document.createElement('a');
  a.href = url;
  a.download = file.filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoking immediately cancels the download in Safari.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function scheduleReport(
  orgCode: string,
  opts: { cadence: string; hourUtc?: number; weekday?: number; dayOfMonth?: number; format?: string },
): Promise<{ describe?: string; email?: string; duplicate?: boolean; message?: string }> {
  return authedPost('/api/premium/zana/report/schedule', { orgCode, ...opts });
}

export async function listReportSchedules(orgCode: string): Promise<ReportSchedule[]> {
  if (!BASE) throw new Error('The reports service is not configured for this build.');
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('Sign in again before running that.');
  const res = await fetch(
    `${BASE}/api/premium/zana/report/schedules?orgCode=${encodeURIComponent(orgCode)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const json = await res.json().catch(() => ({})) as { ok?: boolean; schedules?: ReportSchedule[] };
  if (!json.ok) throw new Error('I could not read your scheduled reports just now.');
  return json.schedules || [];
}

export async function cancelReportSchedule(orgCode: string, id: string): Promise<{ describe?: string }> {
  return authedPost('/api/premium/zana/report/schedule/cancel', { orgCode, id });
}

const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Kenya runs UTC+3, so a UTC hour is not what an owner will read back. */
export function localHourLabel(hourUtc: number): string {
  const local = ((Number(hourUtc) || 0) + 3) % 24;
  return `${String(local).padStart(2, '0')}:00 EAT`;
}

export function describeCadence(s: Pick<ReportSchedule, 'cadence' | 'hourUtc' | 'weekday' | 'dayOfMonth' | 'format'>): string {
  const at = localHourLabel(s.hourUtc);
  const what = s.format === 'csv' ? 'CSV' : 'PDF';
  if (s.cadence === 'daily') return `every day at ${at} (yesterday, ${what})`;
  if (s.cadence === 'weekly') {
    return `every ${WEEKDAY_NAMES[Number(s.weekday ?? 1)] || 'Monday'} at ${at} (last week, ${what})`;
  }
  return `day ${s.dayOfMonth ?? 1} each month at ${at} (last month, ${what})`;
}

/* ── Fuel readings ─────────────────────────────────────────────────────────
   Asks every active bus's driver to log a current tank reading. Lives here
   rather than in a new file because it shares the authed-post plumbing; the
   recipients are resolved server-side from each bus, never sent from here. */
export interface FuelSweepResult {
  asked: number;
  plates: string[];
  skipped: number;
  unreachable: Array<{ plate: string; reason: string }>;
  message: string;
}

export async function requestFuelReadings(
  orgCode: string,
  onlyBuses?: string[],
): Promise<FuelSweepResult> {
  return authedPost<FuelSweepResult>('/api/premium/zana/fuel-readings', {
    orgCode,
    ...(onlyBuses && onlyBuses.length ? { onlyBuses } : {}),
  });
}
